// Accessible drag&drop + file-picker (D-032). Keyboard-operable (the picker is a real <button>),
// labelled, with a visible drag state. Reads the dropped/selected file to bytes and hands it to
// the caller — no network, everything stays in the page (§3.1).

import type { ValidateInput } from '../core/types';

const ACCEPT = '.xml,.pdf,application/pdf,text/xml,application/xml';

export function createDropzone(onFile: (input: ValidateInput) => void): HTMLElement {
  const zone = document.createElement('div');
  zone.className = 'dropzone';
  zone.setAttribute('aria-label', 'Rechnungsdatei hierher ziehen oder auswählen');

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = ACCEPT;
  input.className = 'visually-hidden';
  input.id = 'file-input';
  // The div's aria-label doesn't reach the <input> itself — a screen reader focusing the input
  // directly (e.g. via Tab) needs its own accessible name.
  input.setAttribute('aria-label', 'Rechnungsdatei auswählen (XRechnung XML oder ZUGFeRD/Factur-X PDF)');

  const label = document.createElement('p');
  label.className = 'dropzone__hint';
  label.innerHTML =
    'Ziehen Sie Ihre <strong>XRechnung (XML)</strong> oder <strong>ZUGFeRD/Factur-X (PDF)</strong> hierher';

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'dropzone__button';
  button.textContent = 'Datei auswählen';
  button.addEventListener('click', () => input.click());

  const sub = document.createElement('p');
  sub.className = 'dropzone__sub';
  sub.textContent = 'Die Datei wird ausschließlich in Ihrem Browser geprüft.';

  zone.append(label, button, input, sub);

  async function handleFile(file: File): Promise<void> {
    const bytes = new Uint8Array(await file.arrayBuffer());
    onFile({ bytes, filename: file.name });
  }

  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) void handleFile(file);
    input.value = ''; // allow re-selecting the same file
  });

  zone.addEventListener('dragover', (e) => {
    e.preventDefault();
    zone.classList.add('dropzone--active');
  });
  zone.addEventListener('dragleave', (e) => {
    if (e.target === zone) zone.classList.remove('dropzone--active');
  });
  zone.addEventListener('drop', (e) => {
    e.preventDefault();
    zone.classList.remove('dropzone--active');
    const file = e.dataTransfer?.files?.[0];
    if (file) void handleFile(file);
  });

  return zone;
}
